import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const getPasswordStrength = (pw) => {
  if (!pw) return { level: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (pw.length >= 12) score++;
  if (score <= 1) return { level: 1, label: 'Weak', color: '#ef4444' };
  if (score <= 2) return { level: 2, label: 'Fair', color: '#f59e0b' };
  if (score <= 3) return { level: 3, label: 'Good', color: '#3b82f6' };
  return { level: 4, label: 'Strong', color: '#10b981' };
};

export default function SignUp() {
  const [form, setForm] = useState({ name: '', email: '', password: '', company: '' });
  const [showPw, setShowPw] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const strength = getPasswordStrength(form.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.email || !form.password) {
      setError('Please fill in all required fields');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!agreed) {
      setError('Please agree to the Terms of Service');
      return;
    }
    setLoading(true);
    const result = await register(form.name, form.email, form.password, form.company);
    if (result.success) {
      navigate('/app/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const inputClass = "bg-[#111122] border-white/10 text-white placeholder:text-slate-600 focus:border-[#00d4ff] focus:ring-[#00d4ff]/20 h-11";

  return (
    <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center py-12 px-6 relative">
      <div className="glow-spot w-[400px] h-[400px] bg-[#00d4ff] top-[-100px] left-[-100px] opacity-10" />
      <div className="glow-spot w-[300px] h-[300px] bg-[#3b82f6] bottom-[-100px] right-[-100px] opacity-10" style={{ animationDelay: '2s' }} />

      <motion.div initial="hidden" animate="visible" variants={stagger} className="w-full max-w-md relative">
        <motion.div variants={fadeUp} className="text-center mb-8">
          <Link to="/" className="text-2xl font-black text-white tracking-tight inline-block" style={{ fontFamily: 'Outfit' }}>
            <span className="text-[#00d4ff]">D</span>QP
          </Link>
          <h1 className="text-2xl font-bold text-white mt-4" style={{ fontFamily: 'Outfit' }}>Create your account</h1>
          <p className="text-slate-400 text-sm mt-2">Start exploring your data in minutes</p>
        </motion.div>

        <motion.form variants={fadeUp} data-testid="signup-form" onSubmit={handleSubmit} className="glass-card p-8 space-y-5">
          {error && (
            <div data-testid="signup-error" className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">{error}</div>
          )}
          <div>
            <Label className="text-slate-300 text-sm mb-2 block">Full Name *</Label>
            <Input data-testid="signup-name-input" placeholder="John Doe" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inputClass} />
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-2 block">Email *</Label>
            <Input data-testid="signup-email-input" type="email" placeholder="you@company.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inputClass} />
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-2 block">Password *</Label>
            <div className="relative">
              <Input data-testid="signup-password-input" type={showPw ? 'text' : 'password'} placeholder="Min. 6 characters" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className={`${inputClass} pr-10`} />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {form.password && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex gap-1 flex-1">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-1 flex-1 rounded-full transition-colors" style={{ background: i <= strength.level ? strength.color : 'rgba(255,255,255,0.1)' }} />
                  ))}
                </div>
                <span className="text-xs" style={{ color: strength.color }}>{strength.label}</span>
              </div>
            )}
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-2 block">Company</Label>
            <Input data-testid="signup-company-input" placeholder="Your company name" value={form.company} onChange={e => setForm({...form, company: e.target.value})} className={inputClass} />
          </div>
          <div className="flex items-start gap-3">
            <Checkbox data-testid="signup-terms-checkbox" checked={agreed} onCheckedChange={setAgreed} className="border-white/20 data-[state=checked]:bg-[#00d4ff] data-[state=checked]:border-[#00d4ff] mt-0.5" />
            <span className="text-slate-400 text-sm leading-tight">I agree to the <span className="text-[#00d4ff] cursor-pointer hover:underline">Terms of Service</span> and <span className="text-[#00d4ff] cursor-pointer hover:underline">Privacy Policy</span></span>
          </div>
          <Button data-testid="signup-submit-btn" type="submit" disabled={loading} className="w-full bg-gradient-to-r from-[#00d4ff] to-[#3b82f6] text-[#0a0a1a] font-semibold rounded-full h-11 hover:opacity-90 shadow-[0_0_20px_rgba(0,212,255,0.3)] disabled:opacity-50">
            {loading ? 'Creating account...' : <>Create Account <ArrowRight className="ml-2 w-4 h-4" /></>}
          </Button>
        </motion.form>

        <motion.p variants={fadeUp} className="text-center mt-6 text-slate-500 text-sm">
          Already have an account? <Link to="/signin" data-testid="signup-signin-link" className="text-[#00d4ff] hover:underline">Sign In</Link>
        </motion.p>
      </motion.div>
    </div>
  );
}
